import type { PrimaryPageAction } from "@octopusdeploy/design-system-components";
import type { DeploymentFreezeFilterQueryParameters, DeploymentFreezeOverviewBffResponse, Repository } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import * as React from "react";
import { DeploymentFreezesEmptyState } from "~/areas/configuration/components/DeploymentFreezes/DeploymentFreezesEmptyState";
import DeploymentFreezesPage from "~/areas/configuration/components/DeploymentFreezes/DeploymentFreezesPage";
import { isLicensedFeatureAvailable } from "~/components/LicensedFeature/LicensedFeatureContext";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
export interface DeploymentFreezesLandingPageLoaderData extends DeploymentFreezeOverviewBffResponse {
    globalDeploymentFreezeEnabled: boolean;
}
export interface DeploymentFreezesLandingPageProps {
    loaderData: DeploymentFreezesLandingPageLoaderData;
}
const LandingPageDefaultFilter = {
    Environments: [],
    Projects: [],
    Tenants: [],
    Name: "",
    Status: [],
    PageSize: 30,
    PageNumber: 0,
};
export async function deploymentFreezesLandingPageLoader(repository: Repository): Promise<DeploymentFreezesLandingPageLoaderData> {
    const queryParams: DeploymentFreezeFilterQueryParameters = {
        filterByEnvironments: LandingPageDefaultFilter.Environments,
        filterByName: LandingPageDefaultFilter.Name,
        filterByProjects: LandingPageDefaultFilter.Projects,
        filterByTenants: LandingPageDefaultFilter.Tenants,
        filterByStatus: LandingPageDefaultFilter.Status,
        pageSize: LandingPageDefaultFilter.PageSize.toString(),
        pageNumber: (LandingPageDefaultFilter.PageNumber + 1).toString(),
    };
    const deploymentFreezes = await repository.DeploymentFreezes.getOverviewBff(queryParams);
    return {
        DeploymentFreezes: deploymentFreezes.DeploymentFreezes,
        TotalDeploymentFreezeCount: deploymentFreezes.TotalDeploymentFreezeCount,
        FilteredDeploymentFreezeCount: deploymentFreezes.FilteredDeploymentFreezeCount,
        Filters: deploymentFreezes.Filters,
        globalDeploymentFreezeEnabled: await isLicensedFeatureAvailable("GlobalDeploymentFreeze"),
    };
}
export function DeploymentFreezesLandingPage({ loaderData }: DeploymentFreezesLandingPageProps) {
    if (!loaderData.TotalDeploymentFreezeCount) {
        return <DeploymentFreezesEmptyState globalDeploymentFreezeEnabled={loaderData.globalDeploymentFreezeEnabled}/>;
    }
    return (<DeploymentFreezesPage initialData={{
            Items: loaderData.DeploymentFreezes,
            ItemsPerPage: LandingPageDefaultFilter.PageSize,
            TotalResults: loaderData.TotalDeploymentFreezeCount,
        }} globalDeploymentFreezeEnabled={loaderData.globalDeploymentFreezeEnabled}/>);
}
export const AddDeploymentFreezeAction = (globalDeploymentFreezeEnabled: boolean): PrimaryPageAction => ({
    type: "navigate",
    label: "Add Global Deployment Freeze",
    hasPermissions: isAllowed({ permission: Permission.DeploymentFreezeAdminister }),
    path: links.createDeploymentFreezePage.generateUrl(),
    disabled: !globalDeploymentFreezeEnabled,
    extraContext: globalDeploymentFreezeEnabled ? undefined : "To add a deployment freeze, go to the Projects page, select your project, then select Freezes in the navigation bar.",
});
